import React, { forwardRef, useState } from 'react';

interface SectionCollapseProps {
  label: string;
  children: React.ReactNode;
  sectionId: string;
  defaultOpen?: boolean;
  icon?: React.ReactNode;
  subtitle?: string;
}

const SectionCollapse = forwardRef<HTMLDivElement, SectionCollapseProps>(
  ({ label, children, sectionId, defaultOpen = true, icon, subtitle }, ref) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
      <div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
        ref={ref}
        id={sectionId}
      >
        <button
          type="button"
          className="w-full flex items-center justify-between text-left px-6 py-4 font-semibold text-gray-900 bg-gray-50 hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
        >
          <div className="flex items-center gap-3">
            {icon && <div className="flex-shrink-0 text-gray-400">{icon}</div>}
            <div className="flex flex-col items-start">
              <span className="text-base font-semibold">{label}</span>
              {subtitle && (
                <span className="text-sm font-normal text-gray-600 mt-0.5">
                  {subtitle}
                </span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-6 bg-white border-t border-gray-100">
            {children}
          </div>
        </div>
      </div>
    );
  },
);

SectionCollapse.displayName = 'SectionCollapse';

export default SectionCollapse;
